/**
 * Labeling configuration model.
 *
 * This module deliberately depends on nothing else in the app: `types.ts`
 * imports FROM here so `Layer.labeling` can be typed without a cycle.
 *
 * The model is a LIST of label classes even though the current UI edits only
 * the first one. Esri-style multi-class labeling (a different style per
 * attribute subset) is then an additive change — the persisted shape does not
 * have to be migrated later.
 */

/** Where the text sits relative to the feature. Valid values depend on geometry. */
export type LabelPlacement =
  // Point
  | 'center'
  | 'above'
  | 'below'
  | 'left'
  | 'right'
  // Line
  | 'above-line'
  | 'on-line'
  | 'below-line';

export interface LabelTextStyle {
  /** An id from LABEL_FONTS — not a raw CSS stack, so it stays portable. */
  fontFamily: string;
  fontSize: number;
  fontWeight: 'normal' | '500' | '600' | 'bold';
  fontStyle: 'normal' | 'italic';
  color: string;
}

export interface LabelHalo {
  enabled: boolean;
  color: string;
  /** Radius in pixels. */
  width: number;
}

export interface LabelPlacementConfig {
  type: LabelPlacement;
  offsetX: number;
  offsetY: number;
  /** Lines only: rotate the text to follow the line's bearing. */
  parallel: boolean;
  /** Lines only: repeat the label every N pixels along the line. 0 = once. */
  repeat: number;
}

/**
 * Esri scale semantics, kept exactly:
 *  - `minScale` is the ZOOMED-OUT limit (the larger denominator)
 *  - `maxScale` is the ZOOMED-IN limit (the smaller denominator)
 *  - 0 means "no limit" on that end
 * So 1:50,000 → 1:5,000 is `{ minScale: 50000, maxScale: 5000 }`.
 */
export interface LabelScaleVisibility {
  enabled: boolean;
  minScale: number;
  maxScale: number;
}

export type LabelFilterOperator = '=' | '!=' | '>' | '<' | 'contains';

/**
 * A structured filter rather than a raw expression string.
 *
 * It has to be translated into two different worlds — a client-side predicate
 * and an SLD `ogc:Filter` — and a structured form converts safely to both.
 * A free-text CQL box would need a parser to do the same job.
 */
export interface LabelFilter {
  enabled: boolean;
  field: string;
  operator: LabelFilterOperator;
  value: string;
}

export interface LabelClass {
  id: string;
  /** Shown in the UI once multiple classes exist. */
  name: string;
  /** Field whose value is drawn. Ignored when `expression` is set. */
  field: string;
  /**
   * Optional template, e.g. `{diameter} mm`. Substitution only — see
   * LabelExpression.ts. Never evaluated as code.
   */
  expression: string;
  filter: LabelFilter;
  text: LabelTextStyle;
  halo: LabelHalo;
  placement: LabelPlacementConfig;
  scale: LabelScaleVisibility;
  /** Skip a label when it would overlap one already placed. */
  avoidOverlap: boolean;
}

export interface LayerLabeling {
  /** Schema version, so a future shape change can be migrated deliberately. */
  version: 1;
  enabled: boolean;
  classes: LabelClass[];
}

/* ─────────────────────────────── Fonts ──────────────────────────────────── */

/**
 * Only fonts that render without a network request.
 *
 * Web fonts (Roboto, Open Sans) would need an external stylesheet, which the
 * app's no-hard-coded-URL rule rules out and which would not be available to a
 * GeoServer server-side render anyway.
 */
export const LABEL_FONTS: { id: string; label: string; stack: string; sld: string }[] = [
  {
    id: 'system',
    label: 'System UI',
    stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    sld: 'SansSerif',
  },
  { id: 'arial', label: 'Arial', stack: 'Arial, Helvetica, sans-serif', sld: 'Arial' },
  {
    id: 'helvetica',
    label: 'Helvetica',
    stack: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    sld: 'Helvetica',
  },
  { id: 'verdana', label: 'Verdana', stack: 'Verdana, Geneva, sans-serif', sld: 'Verdana' },
  { id: 'tahoma', label: 'Tahoma', stack: 'Tahoma, Verdana, sans-serif', sld: 'Tahoma' },
  {
    id: 'georgia',
    label: 'Georgia',
    stack: 'Georgia, "Times New Roman", serif',
    sld: 'Georgia',
  },
  {
    id: 'times',
    label: 'Times New Roman',
    stack: '"Times New Roman", Times, serif',
    sld: 'Times New Roman',
  },
  {
    id: 'courier',
    label: 'Courier New',
    stack: '"Courier New", Courier, monospace',
    sld: 'Courier New',
  },
];

export function fontStackFor(id: string): string {
  return LABEL_FONTS.find((font) => font.id === id)?.stack ?? LABEL_FONTS[0].stack;
}

export function sldFontFor(id: string): string {
  return LABEL_FONTS.find((font) => font.id === id)?.sld ?? 'SansSerif';
}

/* ────────────────────────────── Placement ───────────────────────────────── */

/**
 * Placements offered per geometry family.
 *
 * A line layer must never be shown point placements, and vice versa — the UI
 * reads this rather than listing everything and hoping the user picks sensibly.
 */
export const PLACEMENTS_BY_GEOMETRY: Record<string, { id: LabelPlacement; label: string }[]> = {
  point: [
    { id: 'center', label: 'Center' },
    { id: 'above', label: 'Above' },
    { id: 'below', label: 'Below' },
    { id: 'left', label: 'Left' },
    { id: 'right', label: 'Right' },
  ],
  line: [
    { id: 'above-line', label: 'Above line' },
    { id: 'on-line', label: 'On line' },
    { id: 'below-line', label: 'Below line' },
  ],
  polygon: [{ id: 'center', label: 'Center' }],
  unknown: [{ id: 'center', label: 'Center' }],
};

export function defaultPlacementFor(geometry: string): LabelPlacement {
  return (PLACEMENTS_BY_GEOMETRY[geometry] ?? PLACEMENTS_BY_GEOMETRY.unknown)[0].id;
}

/* ─────────────────────────────── Defaults ───────────────────────────────── */

export function createLabelClass(geometry: string, id: string): LabelClass {
  return {
    id,
    name: 'Default',
    field: '',
    expression: '',
    filter: { enabled: false, field: '', operator: '=', value: '' },
    text: {
      fontFamily: 'system',
      fontSize: 12,
      fontWeight: 'normal',
      fontStyle: 'normal',
      color: '#1F2937',
    },
    halo: { enabled: true, color: '#FFFFFF', width: 2 },
    placement: {
      type: defaultPlacementFor(geometry),
      offsetX: 0,
      offsetY: 0,
      parallel: geometry === 'line',
      repeat: 0,
    },
    scale: { enabled: false, minScale: 0, maxScale: 0 },
    avoidOverlap: true,
  };
}

export function createLabeling(geometry: string): LayerLabeling {
  return { version: 1, enabled: false, classes: [createLabelClass(geometry, 'class-1')] };
}

/**
 * Normalises whatever was persisted into a complete config.
 *
 * Modules saved before labeling existed have no `labeling` at all, and a config
 * written by an older version may be missing fields added since — every reader
 * goes through here so the rest of the code can treat the shape as total.
 */
export function normaliseLabeling(
  stored: LayerLabeling | undefined,
  geometry: string,
): LayerLabeling {
  const fresh = createLabeling(geometry);
  if (!stored || !Array.isArray(stored.classes) || stored.classes.length === 0) return fresh;

  const template = fresh.classes[0];
  return {
    version: 1,
    enabled: Boolean(stored.enabled),
    classes: stored.classes.map((labelClass, index) => ({
      ...template,
      ...labelClass,
      id: labelClass.id || `class-${index + 1}`,
      name: labelClass.name || `Class ${index + 1}`,
      filter: { ...template.filter, ...(labelClass.filter ?? {}) },
      text: { ...template.text, ...(labelClass.text ?? {}) },
      halo: { ...template.halo, ...(labelClass.halo ?? {}) },
      placement: { ...template.placement, ...(labelClass.placement ?? {}) },
      scale: { ...template.scale, ...(labelClass.scale ?? {}) },
    })),
  };
}

/**
 * True when a label class names something to draw — regardless of the on/off
 * switch.
 *
 * This is what a viewer-side "show/hide labels" control keys off: offering the
 * toggle on a layer nobody has configured would give the end user a button that
 * visibly does nothing.
 */
export function labelingIsConfigured(labeling: LayerLabeling | undefined): boolean {
  return Boolean(
    labeling?.classes?.some(
      (labelClass) => labelClass.field.trim() !== '' || labelClass.expression.trim() !== '',
    ),
  );
}

/** True when the config would actually draw something right now. */
export function labelingIsActive(labeling: LayerLabeling | undefined): boolean {
  return Boolean(labeling?.enabled) && labelingIsConfigured(labeling);
}

/** One labelled layer's client-side features, ready for the map's label
 *  overlay canvas. */
export interface LabelLayerInput {
  id: string;
  labeling: LayerLabeling;
  features: GeoJSON.Feature[];
}
